
import React, { useState, useEffect } from "react";
import { useStudents } from "@/contexts"; 
import { Student } from "@/services/db";

// Components - fix the import path
import StudentCard from "@/components/students/StudentCard";
import StudentsFilter from "@/components/students/StudentsFilter";
import StudentsPagination from "@/components/students/StudentsPagination";
import EmptyStudentsList from "@/components/students/EmptyStudentsList";
import StudentsLoading from "@/components/students/StudentsLoading";
import StudentsHeader from "@/components/students/StudentsHeader";

const StudentsList = () => {
  const { students, loading, deleteStudent, refreshStudents, initialized } = useStudents();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [sortedStudents, setSortedStudents] = useState<Student[]>([]);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Force refresh students when component mounts and when initialized changes
  useEffect(() => {
    const loadStudents = async () => {
      console.log('StudentsList: Loading students...', { initialized, studentsCount: students.length });
      try {
        await refreshStudents();
        console.log('StudentsList: Students refreshed successfully');
      } catch (error) {
        console.error('StudentsList: Error refreshing students:', error);
      }
    };
    
    if (initialized) {
      loadStudents();
    }
  }, [initialized, refreshStudents]);
  
  // Effect to sort students when sort criteria changes
  useEffect(() => {
    if (!students || students.length === 0) {
      setSortedStudents([]);
      console.log('StudentsList: No students to sort');
      return;
    }

    console.log('StudentsList: Sorting students', students.length);
    const sorted = [...students].sort((a, b) => {
      if (sortBy === "name") {
        return sortOrder === "asc" 
          ? a.name.localeCompare(b.name) 
          : b.name.localeCompare(a.name);
      } else if (sortBy === "programCount") {
        return sortOrder === "asc" 
          ? (a.programs?.length || 0) - (b.programs?.length || 0) 
          : (b.programs?.length || 0) - (a.programs?.length || 0);
      } else if (sortBy === "date") {
        return sortOrder === "asc" 
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return 0;
    });
    
    setSortedStudents(sorted);
    console.log('StudentsList: Sorted students', sorted.length);
  }, [students, sortBy, sortOrder]);

  // Get current students for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(sortedStudents.length / itemsPerPage);

  const handleSort = (criteria: string) => {
    if (sortBy === criteria) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(criteria);
      setSortOrder("asc");
    }
  };

  console.log('StudentsList: Render state', {
    loading,
    initialized,
    studentsCount: students.length,
    sortedStudentsCount: sortedStudents.length,
    currentStudentsCount: currentStudents.length
  });

  return (
    <div className="animate-fade-in">
      <StudentsHeader />
      
      <StudentsFilter 
        sortBy={sortBy} 
        sortOrder={sortOrder} 
        onSort={handleSort} 
      />

      {loading ? (
        <StudentsLoading />
      ) : !students || sortedStudents.length === 0 ? (
        <EmptyStudentsList />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentStudents.map((student) => {
              console.log('StudentsList: Rendering student card for:', student.name, student.id);
              return (
                <StudentCard 
                  key={student.id} 
                  student={student} 
                  onDelete={deleteStudent} 
                />
              );
            })}
          </div>

          <StudentsPagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={paginate} 
          />
        </>
      )}
    </div>
  );
};

export default StudentsList;
